const {Component} = require("react");
const Navbar = require("components/checkout/Navbar");
const OverviewForm = require("components/checkout/OverviewForm");
const Select = require("components/forms/Select");
const userStore = require("stores/UserStore");
const Modal = require("components/modals/Modal");
const Textarea = require("components/forms/Textarea");
const CreditCard = require("components/checkout/CreditCard");
const {classList} = require("helpers/html");
const cartStore = require("stores/CartStore");
const assign = require("object-assign");
const router = require("services/router");
const {decodeObservable, queryObject} = require("helpers/object");
const RecipientSelector = require("components/checkout/RecipientSelector");
const {moneyFormat} = require("helpers/format");
const env = require("services/env");
const Tags = require("components/forms/Tags");
const SubjectPicker = require("components/widgets/SubjectPicker");
const Promise = require("es6-promise");
const Confirm = require("components/modals/Confirm");
const {observer} = require("mobx-react");

@observer
class Overview extends Component {

	constructor(props) {
		super(props);

		this.state = {
			paymentLoading: false,
			paymentSuccess: false,
			paymentType: null,

			purchasingVoucher: false,
			voucherHours: 0,

			subjects: [],
			errors: {}
		};

		this.state.paymentType = this.defaultPaymentOption;
		this.state.voucherHours = this.defaultVoucherOption;
	}

	/////////////////////////////////
	// Accessors
	/////////////////////////////////

	get instantStatus() {
		let instant = false;
		let approvable = false;

		cartStore.get("bookings").forEach(booking => {
			booking.instant ? (instant = true) : (approvable = true);
		});

		if (instant && approvable) {
			return "mixed";
		}

		if (instant) {
			return "instant";
		}

		if (approvable) {
			return "approvable";
		}
	}

	get hasValidVouchers() {
		let count = cartStore.attributes.bookings.length;
		return this.userVouchers.some(voucher => voucher.clips >= count);
	}

	get cardAvailable() {
		const cardType = userStore.get("card_brand");
		const cardDigits = userStore.get("card_last_four");

		return (typeof cardType === "string" && typeof cardDigits === "string");
	}

	get userVouchers() {
		return userStore.get("active_vouchers").filter(voucher => voucher.bookable_id === cartStore.attributes.bookable.id);
	}

	get vouchersAvailable() {
		return this.userVouchers.length > 0;
	}

	get isUsingVoucher() {
		return this.isVoucher(this.state.paymentType) || this.state.purchasingVoucher;
	}

	get hasMultipleRecipients() {
		return this.recipientSelector && (this.recipientSelector.value.length > 1);
	}

	get needsExtraPayment() {
		return this.hasMultipleRecipients && this.isUsingVoucher;
	}

	get currentVoucherClips() {
		if (!this.isUsingVoucher) return 0;

		if (this.state.purchasingVoucher) {
			return this.state.voucherHours;
		}

		return this.userVouchers.find(voucher => {
			return voucher.guid === this.state.paymentType
		}).clips;
	}

	get cardOptions() {
		let options = [
			{
				label: "Betal med andet kort",
				value: "other"
			}
		];

		if (this.cardAvailable) {
			options.unshift({
				label: `${userStore.get("card_brand")} XXXX XXXX XXXX ${userStore.get("card_last_four")}`,
				value: "current"
			});
		}

		return options;
	}

	get paymentOptions() {

		let options = this.cardOptions;

		if (this.vouchersAvailable && !this.state.purchasingVoucher) {
			let count = cartStore.attributes.bookings.length;
			this.userVouchers.reverse().forEach(voucher => {
				options.unshift({
					label: `${voucher.original_clips} klippekort: ${voucher.clips} klip tilbage`,
					value: voucher.guid,
					disabled: count > voucher.clips
				});
			});
		}

		return options;
	}

	get defaultVoucherOption() {
		return this.voucherOptions.find(option => !option.disabled).value;
	}

	get defaultPaymentOption() {
		return this.paymentOptions.find(option => !option.disabled).value;
	}

	get unitPriceExtra() {
		const extraStudentPrice = cartStore.attributes.bookable.prices.filter(price => price.type === "extra_student") [0];
		return this.hasMultipleRecipients ? extraStudentPrice.rate : 0.0;
	}

	get feePercentage() {
		const fallback = [{min_hours: 1, feePercentage: 10}];
		const percents = env.get("options.checkout.feePercentages", fallback);
		const count	= cartStore.attributes.bookings.length;

		const sorter = (a, b) => a.min_hours < b.min_hours; // Highest min_hours first
		const search = (hours) => hours.min_hours <= count;

		return percents
			.sort(sorter)
			.find(search)
			.feePercentage;
	}

	get feeAmount() {
		const feePercentage		= this.feePercentage;
		const unitPriceExtra	= this.unitPriceExtra;
		const unitPriceWhole	= cartStore.attributes.unit_price + unitPriceExtra;
		const unitPriceFee		= Math.round(feePercentage * unitPriceWhole * 0.01);
		
		return unitPriceFee * cartStore.attributes.bookings.length;
	}

	get levelsPool() {
		return decodeObservable(cartStore.get("bookable.levels"));
	}

	get subjectsPool() {
		return decodeObservable(cartStore.get("bookable.subjects"));
	}

	get voucherPrices() {
		return cartStore
				.get("bookable.prices")
				.filter(price => price.voucher_price !== null)
				.map(price => ({hours: price.min_hours, price: price.voucher_price, hourly: price.public_rate}));
	}

	get selectedVoucher() {
		return this.voucherPrices.find(voucher => voucher.hours === this.state.voucherHours);
	}

	get selectedVoucherPrice() {
		const voucher = this.selectedVoucher;

		if (voucher) return voucher.price;
		return null;
	}

	get voucherOptions() {
		let count = cartStore.attributes.bookings.length;
		let options = this.voucherPrices
						.sort((a, b) => a.hours < b.hours ? -1 : 1)
						.map(opt => ({
							value: opt.hours,
							label: `${opt.hours} timer`,
							disabled: opt.hours < count
						}));

		options.unshift({
			value: 0,
			label: 'Ingen klippekort'
		});
		return options;
	}

	error(name) {
		return this.state.errors[name] ? this.state.errors[name][0] : null;
	}

	clearErrors() {
		this.setState({errors: {}});
	}

	showError(name, message) {
		this.setState(state => {
			state.errors[name] = [message];
		});
	}

	/////////////////////////////////
	// Actions & listeners                  
	/////////////////////////////////

	validateInstantBooking() {
		return new Promise((resolve, reject) => {
			if (this.instantStatus === "approvable") {
				return resolve();
			}

			cartStore.validateInstantBookingDistance()
				.then(status => {
					if (status.success) {
						return status;
					}

					return this.instantConfirmationModal.await(
						"Afstanden til læreren er for stor til at kunne bruge øjeblikkelig reservation. Hvis du beslutter dig for at fortsætte, skal læreren manuelt godkende hver af dine bookinger. Ønsker du at fortsætte?"
					);
				})
				.then(resolve)
				.catch(reject);
		});
	}

	validateVoucher(voucher) {
		return new Promise((resolve, reject) => {
			cartStore.validateVoucher(voucher)
				.then(status => {
					if (!status.success) {
						return reject({name: 'payment', value: 'Du kan ikke bruge dette klippekort'});
					}
					resolve(status);
				})
				.catch(reject);
		});
	}

	submitCart(data = {}) {
		return cartStore
			.submit(assign(data, {
				notes: this.notes.value,
				subjects: this.state.subjects.map(subject => subject.id),
				purchase_voucher: this.state.purchasingVoucher,
				voucher_hours: this.state.voucherHours,
				recipients: this.recipientSelector ? this.recipientSelector.value.map(({type, id}) => {
					return {type, id};
				}) : [{type: "user", id: userStore.attributes.id}]
			}))
			.catch(xhr => {
				let err = 'Noget gik galt.';

				if (xhr && xhr.response && xhr.response.data) {
					if (xhr.response.data.errors) {
						this.setState({errors: xhr.response.data.errors});
						throw xhr.response.data.errors;

					} else if (xhr.response.data.message) {
						const msg = xhr.response.data.message;
						err = msg instanceof Array ?
							msg[0] :
							msg;
					}
				}

				this.showError('other', err);
				throw err;

			});
	}

	onOrderCreated(order) {
		setTimeout(function () {
			window.location = router.route("checkout.receipt", {guid: order.guid});
		}, 800);
	}

	isVoucher(value) {
		return this.userVouchers.some(voucher => {
			return voucher.guid === value
		});
	}

	handleSubmit = () => {
		this.clearErrors();

		// Check subjects
		if (this.state.subjects.length === 0) {
			this.showError("subjects", "Fag skal udfyldes.");
			return false;
		}

		return this.form.submit(promise => promise.then((response) => {
			// if a saved card or voucher was selected
			if (this.paymentType && (this.paymentType.value !== "other")) {

				let voucher_id = this.paymentType.value;

				// if voucher was selected
				if (this.isVoucher(voucher_id)) {

					// if extra needed, but no saved card was selected
					if (this.needsExtraPayment && (!this.extraPaymentType || this.extraPaymentType.value === "other")) {
						this.validateInstantBooking()
								.then(() => this.validateVoucher(voucher_id))
								.then(() => this.paymentModal.open());

						return response;
					}

					return this.validateInstantBooking()
								.then(() => this.validateVoucher(voucher_id))
								.then(() => this.submitCart({voucher_id: voucher_id}))
								.then((cartResponse) => this.onOrderCreated(cartResponse.data))
								.catch(err => response);
				}

				// if saved card was selected
				return this.validateInstantBooking()
					.then(() => this.submitCart())
					.then((cartResponse) => this.onOrderCreated(cartResponse.data))
					.catch(err => response);
			}

			// if no options, or "other" was selected
			this.validateInstantBooking()
				.then(() => this.paymentModal.open());

			return response;
		}));
	};

	onPaymentSubmit = e => {
		e.preventDefault();
		e.stopPropagation();

		this.setState({paymentLoading: true});

		this.creditCard.handle()
			.then(token => this.submitCart({card_token: token.id}))
			.catch(err => {
				this.setState({paymentLoading: false});
				this.creditCard.setError(err);
			})
			.then(response => {
				if (
					response.status &&
					response.status === 200 &&
					response.data &&
					response.data.guid
				) {
					this.setState({paymentLoading: false, paymentSuccess: true}, () => {
						this.onOrderCreated(response.data);
					});

				} else {
					this.setState({paymentLoading: false});
				}
			});
	};

	onRecipientChange = () => {
		this.nav.forceUpdate();
	};

	addExtra(subtotal) {
		// extra recipient cost
		if (this.hasMultipleRecipients) {
			const extraStudentPrice = cartStore.attributes.bookable.prices
				.filter(price => price.type === "extra_student") [0];

			subtotal += extraStudentPrice.rate * cartStore.attributes.bookings.length;
		}

		return subtotal;
	}

	processTotal = (total) => {
		total = !this.state.purchasingVoucher && this.isUsingVoucher ?
			0 :
			total;

		total = this.state.purchasingVoucher ?
			this.selectedVoucherPrice :
			total;

		// extra student
		total = this.addExtra(total);

		// fee
		total = this.isUsingVoucher ?
			total :
			total + this.feeAmount;

		return total;
	};

	onSubjectsChange = (subjects) => {
		this.setState({subjects});
	};

	onOpenSubjectsPicker = (e) => {
		e.preventDefault();
		e.stopPropagation();

		this.subjectsPicker.open();
	};

	onVoucherChange = (e) => {
		e.preventDefault();
		e.stopPropagation();

		let val = parseInt(e.target.value);
		if (val !== 0) {
			this.setState({purchasingVoucher: true, voucherHours: val});
			return;
		}

		this.setState({purchasingVoucher: false, voucherHours: 0});
	}

	onPaymentChange = (e) => {
		e.preventDefault();
		e.stopPropagation();

		this.setState({paymentType: e.target.value});
	}

	/////////////////////////////////
	// Rendering
	/////////////////////////////////

	renderSubjects() {
		return (
			<div className="subject-selection">
				<Tags required={true}
							pool={this.subjectsPool}
							value={this.state.subjects}
							label="Fag"
							placeholder="Fag"
							error={this.error("subjects")}
							handleChange={this.onSubjectsChange}/>

				<SubjectPicker ref={el => this.subjectsPicker = el}
											 handleChange={this.onSubjectsChange}
											 levelsPool={this.levelsPool}
											 subjectsPool={this.subjectsPool}
											 subjects={this.state.subjects}/>

				<button type="button" className="btn" onClick={this.onOpenSubjectsPicker}>Vælg fag...</button>
			</div>
		);
	}

	renderRecipients() {
		const user = decodeObservable(userStore.attributes);
		const options = [];

		if (user.subusers.length === 0) {
			return;
		}

		// Push self
		const selfOption = {
			type: "user",
			id: user.id,
			key: `user_${user.id}`,
			name: user.nickname,
			avatar: user.avatar,
			age: user.age,
			levels: user.levels.map(level => level.name)
		};

		options.push(selfOption);

		// Push subusers
		user.subusers.forEach(subuser => {
			options.push({
				type: "subuser",
				id: subuser.id,
				key: `subuser_${subuser.id}`,
				name: subuser.name,
				avatar: subuser.avatar,
				age: subuser.age,
				levels: subuser.level ? [subuser.level.name] : []
			});
		});

		return (
			<RecipientSelector ref={el => this.recipientSelector = el} options={options} value={[selfOption]}
												 handleChange={this.onRecipientChange}/>
		);
	}

	renderForm() {
		return (
			<OverviewForm ref={el => this.form = el} className="checkout-overview">
				<Textarea inputRef={el => this.notes = el} className="full-width" label="Bemærkninger ift. booking"/>
				{this.renderSubjects()}
				{this.renderRecipients()}
			</OverviewForm>
		);
	}

	renderInstantConfirmationModal() {
		if (this.instantStatus !== "approvable") {
			return (
				<Confirm className="instant-confirmation-modal" ref={el => this.instantConfirmationModal = el}/>
			);
		}
	}

	renderPaymentModal() {
		const responsive = !this.state.paymentLoading && !this.state.paymentSuccess;
		const className = classList(
			"payment-modal",
			this.state.paymentLoading ? "loading" : null,
			this.state.paymentSuccess ? "success" : null
		);

		return (
			<Modal ref={el => this.paymentModal = el} className={className} closeable={responsive}>
				<div className="top-bar">
					<p className="title">Du er der næsten, indtast dine kortoplysninger og du er i gang!</p>
				</div>
				<p>Udfyld dine kortoplysninger og kom i gang! Hvis du foretager en forespørgsel, vil pengene først blive
					endeligt betalt, når underviseren har godkendt forespørgslen.</p>

				<div className="payment-body">
					<CreditCard ref={el => this.creditCard = el} required={true}/>
					<button disabled={!responsive} className="btn btn-primary" onClick={this.onPaymentSubmit}>
						<p>Gennemfør betaling</p>

						<div className="loader">
							<svg className="circle-loader" width="40" height="40" version="1.1" xmlns="http://www.w3.org/2000/svg">
								<circle cx="20" cy="20" r="8"/>
							</svg>
						</div>
						<div className="checkmark-container">
							<svg className="checkmark" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 52 52">
								<circle className="checkmark__circle" cx="26" cy="26" r="25" fill="none"/>
								<path className="checkmark__check" fill="none" d="M14.1 27.2l7.1 7.2 16.7-16.8"/>
							</svg>
						</div>
					</button>
				</div>
			</Modal>
		);
	}

	renderRecipientCosts() {
		if (this.hasMultipleRecipients) {
			const extraStudentPrice = cartStore.attributes.bookable.prices.filter(price => price.type === "extra_student") [0];
			const amount = extraStudentPrice.rate * cartStore.attributes.bookings.length;

			return (
				<div className="class-row">
					<p>Tillæg ved mere end 1 elev</p>
					<p className="price">{moneyFormat(amount)}</p>
				</div>
			);
		}
	}

	renderFee() {
		return (
			<div className="class-row">
				<p>Servicegebyr</p>
				<p className="price">{moneyFormat(this.feeAmount)}</p>
			</div>
		);
	}

	renderNavExtras = () => {
		return (
			<div className="payment-nav-extras">
				{this.renderRecipientCosts()}
				{this.renderFee()}
			</div>
		);
	};

	renderVoucherOptions = () => {
		return (
			<Select inputRef={el => this.voucherType = el} label="Køb klippekort" options={this.voucherOptions}
							error={this.error('voucher')} onChange={this.onVoucherChange} />
		);
	}

	renderNavOptions = () => {
		if (this.cardAvailable || (this.vouchersAvailable && !this.state.purchasingVoucher)) {
			const main = (<Select inputRef={el => this.paymentType = el} label="Betalingsoplysninger" options={this.paymentOptions}
								error={this.error('payment')} onChange={this.onPaymentChange}
								value={this.state.paymentType}/>
			);

			const extra = (!this.state.purchasingVoucher && this.needsExtraPayment && this.cardAvailable) ?
				(<Select inputRef={el => this.extraPaymentType = el} label="Betal tillæg med:" options={this.cardOptions} />):
				null;

			return (
				<React.Fragment>
					{main}
					{extra}
				</React.Fragment>
			);
		}
	};

	renderVoucherPrice() {
		if (!this.state.purchasingVoucher) return;

		const voucher = this.selectedVoucher;
		const base = voucher.hourly * voucher.hours;
		const fee = voucher.price - base;

		return (
			<React.Fragment>
				<div className="class-row">
					<p>Klippekort:</p>
					<p className="price">{moneyFormat(base)}</p>
				</div>
				<div className="class-row">
					<p>Servicegebyr:</p>
					<p className="price">{moneyFormat(fee)}</p>
				</div>
			</React.Fragment>
		);
	}

	renderVoucherInfo = () => {
		if (!this.state.purchasingVoucher && !this.needsExtraPayment) return;

		const extraFees = this.needsExtraPayment ?
			this.renderRecipientCosts() :
			null;

		return (
			<div className="classes">
				<div className="payment-extras">
					{this.renderVoucherPrice()}
					{extraFees}
				</div>
			</div>
		);
	}

	renderNavFooter = () => {
		const tos = (<p>Jeg accepterer <a target="_blank" rel="noopener noreferrer" href={router.route('terms')}>regler og vilkår</a> ved at fortsætte</p>);
		let vouch;
		let err;

		if (this.state.purchasingVoucher) {
			const expMonths = env.get("options.vouchers.lifespan", 12);
			vouch = (<p>OBS: Alle klippekort uldøber efter {expMonths} måneder.</p>);
		}

		if (this.error('other')) {
			err = <p className="error">{this.error('other')}</p>
		}

		return (
			<React.Fragment>
				{err}
				{tos}
				{vouch}
			</React.Fragment>
		);
	};

	renderAdditionalInformation() {
		switch (this.instantStatus) {
			case "instant":
				return (<p>Du er nu ved at booke undervisning på de valgte tidspunkter. Bemærk at bookingen er endelig, og at en
					senere aflysning kan resultere i gebyrer. Når du har betalt vil du have mulighed for at koordinere nærmere med
					din underviser via vores indbyggede chat. Vi ønsker dig god fornøjelse med undervisningen, og håber at du vil
					benytte os igen.</p>);

			case "mixed":
				return (
					<div>
						<p>Du er nu ved at booke specifikke undervisningstimer, samt forespørge om yderligere tidspunkter der først
							skal bekræftes. Følgende gør sig gældende:</p>
						<p>Forespørgsel: Underviseren vil automatisk blive notificeret, og vil snarest muligt besvare din
							henvendelse. Bemærk at underviseren kan vælge at godkende eller afvise forespørgslen. Betalingen vil blot
							blive reserveret nu, og bliver ikke trukket før underviseren har godkendt og bekræftet din forespørgsel.
							Du vil få besked så snart underviseren har svaret. Når underviseren har godkendt, vil du have mulighed for
							at koordinere nærmere via vores indbyggede chat. Vi ønsker dig god fornøjelse med undervisningen, og håber
							at du vil benytte os igen.</p>
						<p>Booking: Du er nu ved at booke undervisning på de valgte tidspunkter. Bemærk at bookingen er endelig, og
							at en senere aflysning kan resultere i gebyrer. Når du har betalt vil du have mulighed for at koordinere
							nærmere med din underviser via vores indbyggede chat. Vi ønsker dig god fornøjelse med undervisningen, og
							håber at du vil benytte os igen.</p>
					</div>
				);

			case "approvable":
			default:
				return (<p>Du er nu ved at forespørge om en undervisning på de valgte tidspunkter. Underviseren vil automatisk
					blive notificeret, og vil snarest muligt besvare din henvendelse. Bemærk at underviseren kan vælge
					at godkende eller afvise forespørgslen. Betalingen vil blot blive reserveret nu, og bliver ikke
					trukket før underviseren har godkendt og bekræftet din forespørgsel. Du vil få besked så snart
					underviseren har svaret. Når underviseren har godkendt, vil du have mulighed for at koordinere
					nærmere via vores indbyggede chat. Vi ønsker dig god fornøjelse med undervisningen, og håber at du
					vil benytte os igen.</p>);
		}
	}

	render() {
		return (
			<section className="checkout">
				<div className="checkout-container container">
					<div className="checkout-inner">

						<div className="breadcrumb animate slide-in">
							<p className="active">1. Overblik og oplyninger</p>
							<p>2. Betaling</p>
						</div>

						<div className="overview animate slide-in">

							<div className="overview-box">
								<div className="top-bar">
									<span className="sub-title-bar">Betaling</span>
									<h1>Overblik og betaling</h1>
								</div>
								<div className="overview-content">
									<div className="top-bar-content">
										<p>Generelle informationer</p>
									</div>

									{this.renderForm()}
								</div>
								<div className="overview-footer">
									<span className="sub-title-bar">Information</span>
									{this.renderAdditionalInformation()}
								</div>
							</div>
						</div>

						<div className="react-component">
							<Navbar ref={el => this.nav = el}
											store={cartStore}
											showClips={this.isUsingVoucher}
											purchasingVoucher={this.purchasingVoucher}
											availableClips={this.currentVoucherClips}
											submitLabel="Accepter og fortsæt"
											processTotal={this.processTotal}
											handleSubmit={this.handleSubmit}
											renderExtras={this.renderNavExtras}
											renderOptions={this.renderNavOptions}
											renderPreOptions={this.renderVoucherOptions}
											renderFooter={this.renderNavFooter}
											renderVoucher={this.renderVoucherInfo} />
						</div>

						{this.renderPaymentModal()}
						{this.renderInstantConfirmationModal()}
					</div>
				</div>
			</section>
		);
	}

}

module.exports = Overview;



// WEBPACK FOOTER //
// ./resources/js/components/checkout/Overview.js